import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import type {
  OpfsWorkerMessage,
  OpfsWorkerResponse,
} from "~/features/cache/opfs";
import { opfsWorkerResponseSchema } from "~/features/cache/opfs";

type OpfsContext = {
  postWorkerMessage(message: OpfsWorkerMessage): void;
  onMessage(callback: ResponseListener): () => void;
};

const Context = createContext<OpfsContext | null>(null);

type ResponseListener = (message: OpfsWorkerResponse) => void;

export const OpfsWorkerProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const worker = useRef<Worker | null>(null);
  const listeners = useRef<ResponseListener[]>([]);

  useEffect(() => {
    worker.current = new Worker(new URL("./opfs.worker.ts", import.meta.url), {
      type: "module",
    });

    worker.current?.addEventListener("message", (event) => {
      const data = opfsWorkerResponseSchema.safeParse(event.data);
      if (data.success) {
        console.debug("Received message from OPFS worker", data.data);
        for (const cb of listeners.current) {
          cb(data.data);
        }
      } else {
        console.error("Received invalid message from OPFS worker", data.error);
      }
    });

    return () => {
      worker.current?.terminate();
    };
  }, []);

  const context = useMemo<OpfsContext>(
    () => ({
      postWorkerMessage(message: OpfsWorkerMessage) {
        console.debug("Posting message to OPFS worker", message.type);
        worker.current?.postMessage(message);
      },
      onMessage(callback: ResponseListener) {
        listeners.current.push(callback);

        return () => {
          listeners.current = listeners.current.filter((cb) => cb !== callback);
        };
      },
    }),
    [],
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const useOpfsWorker = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      "useOpfsWorker must be used within a OpfsWorkerProvider component",
    );
  }

  return context;
};
